/**
 * A small RFC 4180 CSV reader/writer.
 *
 * Hand-rolled rather than pulled from npm because this workspace resolves one
 * dependency from a private registry, so `npm install` cannot add a package
 * without that credential. The trade-off is accepted knowingly: the risky part
 * of CSV is not the parser but the cases people skip, so the budget went into
 * csv.test.ts — quoted separators, embedded newlines, doubled quotes, CRLF, a
 * BOM, ragged rows and an unterminated quote are all pinned there.
 *
 * Why a character state machine and not `split`: a naive split corrupts a row
 * containing a quoted newline *and every row after it*, and does so silently —
 * the user gets 300 leads out of 500 with no error to explain the other 200.
 */

export interface ParsedCsv {
  /** Heading row, trimmed. Empty when the file had nothing usable. */
  headers: string[];
  /** Data rows, padded to the heading count so column positions stay aligned. */
  rows: string[][];
  /**
   * 1-based line number in the original file for each entry in `rows`.
   *
   * Not the same as the index: blank lines are dropped and a quoted value can
   * span several lines, so `rows[3]` is often not the file's 5th line. Telling a
   * user to fix "row 347" is only useful if 347 is what their spreadsheet shows.
   */
  rowLines: number[];
  /** The separator actually used, so the UI can show it. */
  delimiter: string;
  /**
   * Worksheet name, when the rows came from a spreadsheet rather than a CSV.
   * Absent for CSV, which is how the UI knows to hide the separator controls.
   */
  sheetName?: string;
  /** Every worksheet in the workbook, so the user can pick a different one. */
  sheetNames?: string[];
}

export interface ParseCsvOptions {
  /** Force a separator instead of detecting one. */
  delimiter?: string;
  /**
   * The text qualifier that wraps a value containing the separator, a newline,
   * or the qualifier itself. Defaults to a double quote, which is what almost
   * every export writes. Pass `''` to disable quoting entirely, so that every
   * character is literal data.
   */
  quote?: string;
}

/** What a spreadsheet export means by "text qualifier" unless told otherwise. */
export const DEFAULT_QUOTE = '"';

const CANDIDATE_DELIMITERS = [',', ';', '\t', '|'] as const;

/** Excel's "CSV UTF-8" writes a byte-order mark; it must not become part of the first heading. */
export function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/**
 * Guess the separator from the heading row.
 *
 * Quoted sections are blanked before counting, so a "Last, First" heading cannot
 * outvote the real separator — the single most common way detection goes wrong
 * on a real export.
 */
/**
 * Blank out every qualified section of a line, preserving its length.
 *
 * Used only by delimiter detection: a `"Last, First"` heading must not let its
 * inner comma outvote the real separator. Written as a scan rather than a regex
 * because the qualifier is user-configurable and building a regex from it would
 * mean escaping arbitrary input.
 */
function maskQuoted(line: string, quote: string): string {
  let out = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === quote) {
      // A doubled qualifier is an escaped literal, not a boundary.
      if (inQuotes && line[i + 1] === quote) {
        out += '__';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      out += '_';
      continue;
    }

    out += inQuotes ? '_' : char;
  }

  return out;
}

export function detectDelimiter(text: string, quote: string = DEFAULT_QUOTE): string {
  const firstLine = stripBom(text).split(/\r\n|\n|\r/, 1)[0] ?? '';
  if (!firstLine) return ',';

  // Blank out anything inside the qualifier before counting, so a separator
  // that is really data - a "Last, First" heading - cannot outvote the real
  // one. With quoting disabled there is nothing to mask.
  const masked = quote === '' ? firstLine : maskQuoted(firstLine, quote);

  let best = ',';
  let bestCount = 0;

  for (const candidate of CANDIDATE_DELIMITERS) {
    const count = masked.split(candidate).length - 1;
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }

  return bestCount > 0 ? best : ',';
}

/**
 * Parse a whole CSV document.
 *
 * Rows shorter than the heading row are padded with empty strings rather than
 * left short, so a row that ends early cannot shift later values into the wrong
 * column. Longer rows keep their extra cells — dropping them would discard data
 * the user can still see in their file.
 */
export function parseCsv(text: string, options: ParseCsvOptions = {}): ParsedCsv {
  const source = stripBom(text ?? '');
  const quote = options.quote ?? DEFAULT_QUOTE;
  const delimiter = options.delimiter ?? detectDelimiter(source, quote);

  if (source.trim() === '') {
    return { headers: [], rows: [], rowLines: [], delimiter: options.delimiter ?? ',' };
  }

  const { records, lines } = readRecords(source, delimiter, quote);
  if (records.length === 0) {
    return { headers: [], rows: [], rowLines: [], delimiter };
  }

  const headers = (records[0] ?? []).map((cell) => cell.trim());
  const width = headers.length;

  const rows = records.slice(1).map((record) =>
    record.length < width
      ? [...record, ...Array<string>(width - record.length).fill('')]
      : record,
  );

  return { headers, rows, rowLines: lines.slice(1), delimiter };
}

/**
 * The state machine. Walks the document once, character by character.
 *
 * `inQuotes` is the whole trick: while it is true a delimiter, CR or LF is
 * ordinary text rather than a boundary, which is what keeps a multi-line note
 * from splitting its own row.
 */
function readRecords(
  source: string,
  delimiter: string,
  quote: string,
): { records: string[][]; lines: number[] } {
  const records: string[][] = [];
  const lines: number[] = [];
  let record: string[] = [];
  let field = '';
  let inQuotes = false;
  let fieldWasQuoted = false;
  // 1-based source line, advanced by every newline including ones inside quotes.
  let line = 1;
  let recordStartLine = 1;

  const endField = () => {
    // Only unquoted values are trimmed: whitespace the author deliberately
    // quoted is theirs to keep.
    record.push(fieldWasQuoted ? field : field.trim());
    field = '';
    fieldWasQuoted = false;
  };

  const endRecord = () => {
    endField();
    // Drop the blank record a trailing newline produces, and any blank line in
    // the middle — but never a row that merely has empty cells.
    if (!(record.length === 1 && record[0] === '')) {
      records.push(record);
      lines.push(recordStartLine);
    }
    record = [];
    recordStartLine = line;
  };

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];

    if (inQuotes) {
      if (char === quote) {
        if (source[i + 1] === quote) {
          field += quote;
          i += 1;
        } else {
          inQuotes = false;
        }
        continue;
      }

      // Normalize line endings inside a quoted value so a note reads the same
      // whichever platform wrote the file.
      if (char === '\r' || char === '\n') {
        if (char === '\r' && source[i + 1] === '\n') i += 1;
        field += '\n';
        line += 1;
        continue;
      }

      field += char;
      continue;
    }

    // The qualifier only opens quoted mode at the START of a field. Anywhere
    // else it is literal text: clinical notes are full of inch marks
    // (`5" scar`), and treating one as an opening quote swallows every
    // remaining row into that cell — silently, with no error and no short row
    // to notice. This holds for whatever qualifier is configured, and
    // `quote === ''` disables the branch entirely.
    if (quote !== '' && char === quote && field === '' && !fieldWasQuoted) {
      inQuotes = true;
      fieldWasQuoted = true;
      continue;
    }

    if (char === delimiter) {
      endField();
      continue;
    }

    if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i += 1;
      line += 1;
      endRecord();
      continue;
    }

    field += char;
  }

  // An unterminated final quote leaves `inQuotes` true; flush what we have
  // rather than throwing away the last row.
  if (field !== '' || record.length > 0) {
    endRecord();
  }

  return { records, lines };
}

/** Does this value need quoting to survive a round trip? */
function needsQuoting(value: string, delimiter: string): boolean {
  return (
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r') ||
    value.trim() !== value
  );
}

/**
 * Serialize rows back to CSV — used to hand the user the rows that were not
 * imported, so they can fix them in their own spreadsheet and try again.
 */
export function toCsv(rows: string[][], delimiter = ','): string {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          const value = cell ?? '';
          return needsQuoting(value, delimiter)
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        })
        .join(delimiter),
    )
    .join('\r\n');
}

/**
 * Stop a spreadsheet treating a cell as a formula.
 *
 * Excel executes any cell starting with = + - @ (or a lone tab/CR) on open, so
 * a leading apostrophe forces such text back to text. Applied by the caller to
 * the text it exports, not inside `toCsv`, which stays a faithful RFC 4180
 * writer its round-trip test can rely on.
 */
export function defuseFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value ?? '') ? `'${value}` : value;
}
